/**
 * Objective evaluation at DEBRIEF (DESIGN §6.16).
 */
import type { SeatState, SessionState, TableState } from '../types.ts';
import { emit, seatedRoles, type Ctx } from './common.ts';

export interface ObjectiveResult {
  key: string;
  achieved: boolean;
  detail: string;
}

/** Evaluate team objectives from shared table history. */
function evaluateTeamObjective(s: SessionState, t: TableState, key: string): ObjectiveResult | null {
  const last = s.clock.lastTurn;
  const r = (achieved: boolean, detail: string): ObjectiveResult => ({ key, achieved, detail });
  switch (key) {
    case 'CALL_EARLY': {
      const first = t.tableStats.callTurns[0];
      return r(first !== undefined && first < Math.ceil(last / 2), first === undefined ? 'No call' : `Called in turn ${first}`);
    }
    case 'TIMEOUT_EARLY':
      return r(t.timeOut.turn !== null && t.timeOut.turn < last - 2, t.timeOut.turn === null ? 'Not used' : `Used in turn ${t.timeOut.turn}`);
    case 'NO_OVERFLOW_DELAYS':
      return r(t.dashboard.overflowDelays === 0, `${t.dashboard.overflowDelays} discharges delayed by workload`);
    case 'AHEAD_OF_CURVE': {
      const first = t.tableStats.firstOverflowTurn;
      const early = t.bookings.filter(booking => first === null || booking.bookedTurn < first).length;
      return r(t.bookings.length > 0 && early === t.bookings.length, `${early} of ${t.bookings.length} requests made before the beds ran out`);
    }
    default:
      return null;
  }
}

export function evaluateObjective(s: SessionState, t: TableState, st: SeatState): ObjectiveResult | null {
  const key = st.objective;
  if (!key) return null;
  const teamResult = evaluateTeamObjective(s, t, key);
  if (teamResult) return teamResult;
  const last = s.clock.lastTurn;
  const sum = (o: Record<number, number>) => Object.values(o).reduce((a, b) => a + b, 0);
  const r = (achieved: boolean, detail: string): ObjectiveResult => ({ key, achieved, detail });
  switch (key) {
    case 'THROUGHPUT': {
      const n = st.stats.discharges + st.stats.referralsOut;
      return r(n >= 4, `${n} discharged or referred`);
    }
    case 'BOLD':
      return r(st.stats.unconfirmedTreats >= 4 && st.stats.unconfirmedWrongHighCrit === 0, `${st.stats.unconfirmedTreats} treated unconfirmed, ${st.stats.unconfirmedWrongHighCrit} went HIGH/CRITICAL`);
    case 'THOROUGH':
      return r(st.stats.confirmedBeforeTreat >= 6, `${st.stats.confirmedBeforeTreat} confirmed before treating`);
    case 'MAYORS_COUSIN':
      return r(st.stats.vipCompleted, st.stats.vipCompleted ? 'You discharged him' : 'Not by you');
    case 'TEACHING': {
      const quiet = (st.stats.actionsByTurn[last - 3] ?? 0) === 0;
      const noDeaths = (st.stats.deathsByTurn[last - 2] ?? 0) === 0;
      return r(quiet && noDeaths, `${quiet ? 'no actions' : 'actions'} in turn ${last - 3}; ${noDeaths ? 'no deaths' : 'a death'} in turn ${last - 2}`);
    }
    case 'SELF_RELIANT':
      return r(t.callsForHelp === 0, `${t.callsForHelp} calls for help`);
    case 'FRUGAL':
      return r(t.tableStats.scansTotal <= 4, `${t.tableStats.scansTotal} scans`);
    case 'CLEAN_SHEET':
      return r(st.stats.deteriorationsOnList === 0, `${st.stats.deteriorationsOnList} deteriorations on your list`);
    case 'PROCEDURALIST': {
      const n = st.role === 'SURG' ? st.stats.theatreSteps : st.stats.anaesthetics;
      return r(n >= 3, `${n} Theatre steps`);
    }
    case 'EARLY_FINISH': {
      const n = st.stats.patientsAtStartOfTurn[last] ?? 0;
      return r(n === 0, `${n} patients at the start of the last turn`);
    }
    case 'AMBITION':
      return r(t.lead === st.role, t.lead === st.role ? 'You held the Lead' : `${t.lead} held the Lead`);
    case 'CONSULTANT':
      return r(st.stats.helpGiven >= 4, `${st.stats.helpGiven} actions on other lists`);
    case 'TERRITORIAL':
      return r(st.stats.helpReceived === 0, `${st.stats.helpReceived} helping actions on your list`);
    case 'SOVEREIGN':
      return r(t.tableStats.divertTurns === 0, `${t.tableStats.divertTurns} turns diverting`);
    case 'GOOD_NEIGHBOUR':
      return r(t.dashboard.accepted >= 3, `${t.dashboard.accepted} received from elsewhere`);
    case 'FLOW': {
      const ok = t.tableStats.stepDownLate === 0 && Object.keys(t.tableStats.stepDownWaiting).length === 0;
      return r(ok, `${t.tableStats.stepDownOnTime} on time, ${t.tableStats.stepDownLate} late`);
    }
    case 'HOME_EARLY':
      return r(st.stats.earlyPassed >= 3 && st.stats.earlyBounced === 0, `${st.stats.earlyPassed} passed, ${st.stats.earlyBounced} bounced`);
    default:
      void sum;
      return r(false, 'Unknown card');
  }
}

export function evaluateObjectives(ctx: Ctx): void {
  for (const t of ctx.s.tables) {
    let n = 0;
    for (const role of seatedRoles(t)) {
      const res = evaluateObjective(ctx.s, t, t.seats[role]!);
      if (!res) continue;
      if (res.achieved) n++;
      emit(ctx, { type: 'OBJECTIVE', table: t.no, seat: role, severity: 0, text: `${role}: ${res.key} ${res.achieved ? 'achieved' : 'not achieved'} (${res.detail}).`, data: { ...res } });
    }
    t.dashboard.objectives = n;
  }
}
